import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Clock, Target, ArrowRight, Volume2, Globe, Search, Grid, List, Home, ArrowLeft } from 'lucide-react';
import AdvancedVoiceSelector from '../components/english/AdvancedVoiceSelector';
import { useVoice } from '../contexts/VoiceContext';
import { englishContentService } from '../services/english/englishContentService';

interface LessonInfo {
  lop: number;
  chuong: number;
  bai: number;
  title: string;
  vietnameseTitle: string;
  description: string;
  difficulty: string;
  estimatedTime: number;
}

const PhysicsEnglishIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<LessonInfo[]>([]);
  const [filteredLessons, setFilteredLessons] = useState<LessonInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLop, setSelectedLop] = useState<string>('all');
  const [selectedChuong, setSelectedChuong] = useState<string>('all');
  const [selectedBai, setSelectedBai] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Use voice context
  const { setSelectedVoice } = useVoice();

  useEffect(() => {
    const loadLessons = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load all available lessons using the optimized service
        const allLessons = await englishContentService.getAllLessons();

        setLessons(allLessons);
        setFilteredLessons(allLessons);
      } catch (err) {
        console.error('Error loading lessons:', err);
        setError('Failed to load lessons');
      } finally {
        setLoading(false);
      }
    };

    loadLessons();
  }, []);

  // Get available chuong for selected lop
  const getAvailableChuong = (): number[] => {
    if (selectedLop === 'all') return [];
    const chuongSet = new Set<number>();
    lessons
      .filter(lesson => lesson.lop === parseInt(selectedLop))
      .forEach(lesson => chuongSet.add(lesson.chuong));
    return Array.from(chuongSet).sort((a, b) => a - b);
  };

  // Get available bai for selected lop and chuong
  const getAvailableBai = (): number[] => {
    if (selectedLop === 'all' || selectedChuong === 'all') return [];
    const baiSet = new Set<number>();
    lessons
      .filter(lesson =>
        lesson.lop === parseInt(selectedLop) &&
        lesson.chuong === parseInt(selectedChuong)
      )
      .forEach(lesson => baiSet.add(lesson.bai));
    return Array.from(baiSet).sort((a, b) => a - b);
  };

  // Filter lessons based on selected filters
  useEffect(() => {
    let filtered = lessons;

    // Filter by lop
    if (selectedLop !== 'all') {
      filtered = filtered.filter(lesson => lesson.lop === parseInt(selectedLop));
    }

    // Filter by chuong
    if (selectedChuong !== 'all') {
      filtered = filtered.filter(lesson => lesson.chuong === parseInt(selectedChuong));
    }

    // Filter by bai
    if (selectedBai !== 'all') {
      filtered = filtered.filter(lesson => lesson.bai === parseInt(selectedBai));
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(lesson =>
        lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lesson.vietnameseTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lesson.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredLessons(filtered);
  }, [lessons, selectedLop, selectedChuong, selectedBai, searchTerm]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getClassColor = (lop: number) => {
    switch (lop) {
      case 10:
        return 'bg-green-500';
      case 11:
        return 'bg-blue-500';
      case 12:
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Lesson Card Component
  const LessonCard: React.FC<{ lesson: LessonInfo }> = ({ lesson }) => (
    <Link
      to={`/physics-english/${lesson.lop}/${lesson.chuong}/${lesson.bai}`}
      className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 group border border-gray-100"
    >
      <div className="p-6">
        {/* Class Badge */}
        <div className="flex items-center justify-between mb-4">
          <div className={`px-3 py-1 rounded-full text-white text-sm font-medium ${getClassColor(lesson.lop)}`}>
            Chương {lesson.chuong} - Bài {lesson.bai}
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(lesson.difficulty)}`}>
            {lesson.difficulty}
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
          {lesson.title}
        </h3>
        <p className="text-sm text-gray-600 mb-3">{lesson.vietnameseTitle}</p>

        {/* Description */}
        <p className="text-gray-700 text-sm mb-4 line-clamp-2">
          {lesson.description}
        </p>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{lesson.estimatedTime} phút</span>
          </div>
          <div className="flex items-center space-x-1">
            <Volume2 className="w-3 h-3" />
            <span>TTS enabled</span>
          </div>
        </div>

        {/* Arrow */}
        <div className="flex justify-end mt-4">
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
        </div>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Physics English lessons...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😞</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Lessons</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary inline-flex items-center px-6 py-3 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to Home Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-md hover:shadow-lg hover:bg-gray-50 transition-all duration-300 transform hover:scale-105"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Về trang chủ</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Globe className="w-12 h-12 text-blue-600 mr-4" />
            <h1 className="text-4xl font-bold text-gray-900">Physics English</h1>
          </div>
          <p className="text-xl text-gray-600 mb-6">
            Learn Physics vocabulary and concepts in English with Text-to-Speech
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <Volume2 className="w-4 h-4" />
              <span>High-quality Microsoft voices</span>
            </div>
            <div className="flex items-center space-x-1">
              <BookOpen className="w-4 h-4" />
              <span>{lessons.length} lessons available</span>
            </div>
          </div>
        </div>

        {/* Voice Selector */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <Volume2 className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Chọn giọng đọc</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Giọng đọc bạn chọn ở đây sẽ được áp dụng cho tất cả các bài học English Physics
          </p>
          <AdvancedVoiceSelector onVoiceChange={(voice) => setSelectedVoice(voice)} />
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Tìm kiếm và Lọc</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Tìm kiếm bài học English Physics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            {/* Filter by Lop */}
            <select
              value={selectedLop}
              onChange={(e) => {
                setSelectedLop(e.target.value);
                setSelectedChuong('all');
                setSelectedBai('all');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tất cả lớp</option>
              <option value="10">Lớp 10</option>
              <option value="11">Lớp 11</option>
              <option value="12">Lớp 12</option>
            </select>

            {/* Filter by Chuong */}
            <select
              value={selectedChuong}
              onChange={(e) => {
                setSelectedChuong(e.target.value);
                setSelectedBai('all');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={selectedLop === 'all'}
            >
              <option value="all">Tất cả chương</option>
              {getAvailableChuong().map(chuong => (
                <option key={chuong} value={chuong}>Chương {chuong}</option>
              ))}
            </select>

            {/* Filter by Bai */}
            <select
              value={selectedBai}
              onChange={(e) => setSelectedBai(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={selectedLop === 'all' || selectedChuong === 'all'}
            >
              <option value="all">Tất cả bài</option>
              {getAvailableBai().map(bai => (
                <option key={bai} value={bai}>Bài {bai}</option>
              ))}
            </select>
          </div>

          {/* Results count */}
          <div className="mt-4">
            <p className="text-gray-600">
              Tìm thấy <span className="font-semibold text-blue-600">{filteredLessons.length}</span> bài học
            </p>
          </div>
        </div>

        {/* Lessons by Class */}
        {filteredLessons.length > 0 ? (
          <div className="space-y-8">
            {/* Lớp 10 */}
            {filteredLessons.filter(l => l.lop === 10).length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                  <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center mr-3">
                    10
                  </div>
                  Lớp 10 ({filteredLessons.filter(l => l.lop === 10).length} bài)
                </h2>
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                  : 'space-y-4'
                }>
                  {filteredLessons.filter(l => l.lop === 10).map((lesson) => (
                    <LessonCard key={`${lesson.lop}-${lesson.chuong}-${lesson.bai}`} lesson={lesson} />
                  ))}
                </div>
              </div>
            )}

            {/* Lớp 11 */}
            {filteredLessons.filter(l => l.lop === 11).length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-blue-600 mb-6 flex items-center">
                  <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center mr-3">
                    11
                  </div>
                  Lớp 11 ({filteredLessons.filter(l => l.lop === 11).length} bài)
                </h2>
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                  : 'space-y-4'
                }>
                  {filteredLessons.filter(l => l.lop === 11).map((lesson) => (
                    <LessonCard key={`${lesson.lop}-${lesson.chuong}-${lesson.bai}`} lesson={lesson} />
                  ))}
                </div>
              </div>
            )}

            {/* Lớp 12 */}
            {filteredLessons.filter(l => l.lop === 12).length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-purple-600 mb-6 flex items-center">
                  <div className="w-10 h-10 rounded-full bg-purple-500 text-white flex items-center justify-center mr-3">
                    12
                  </div>
                  Lớp 12 ({filteredLessons.filter(l => l.lop === 12).length} bài)
                </h2>
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                  : 'space-y-4'
                }>
                  {filteredLessons.filter(l => l.lop === 12).map((lesson) => (
                    <LessonCard key={`${lesson.lop}-${lesson.chuong}-${lesson.bai}`} lesson={lesson} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Lessons Available</h2>
            <p className="text-gray-600">
              English lessons are being prepared. Please check back later.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">🎤 Text-to-Speech Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
              <div className="flex items-center justify-center space-x-2">
                <Volume2 className="w-4 h-4 text-blue-500" />
                <span>Microsoft voices</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Target className="w-4 h-4 text-green-500" />
                <span>Click to speak</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <BookOpen className="w-4 h-4 text-purple-500" />
                <span>Vocabulary learning</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicsEnglishIndexPage;
